import { useEffect, useMemo, useState } from "react";
import { ScheduleCalendar } from "./ScheduleCalendar";
import { ScreeningAvailabilitySheet } from "./ScreeningAvailabilitySheet";
import { TasksContainerExpanded } from "./TasksContainerExpanded";
import { TasksContainerMini } from "./TasksContainerMini";

import { useGetCalendarWeek } from "@/hooks/queries/useGetCalendarWeek";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { ALL_USERS_KEY } from "@/lib/constants";
import { calendarPrototype } from "@/lib/calendarPrototype";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { getCandidacyLink } from "@/lib/notificationLinks";
import { isUserTyping } from "@/lib/utils";
import {
  CommsToSendCandidacy,
  DashboardInterview,
  DashboardSchedulePanelView,
  DashboardSelectedUser,
  DashboardTask,
  InterviewType,
} from "@/types";

const DAYS_IN_WEEK = 7;

const shiftWeeks = (weekStart: Date, weeks: number): Date => {
  const shifted = new Date(weekStart);
  shifted.setDate(shifted.getDate() + weeks * DAYS_IN_WEEK);
  return shifted;
};

// We query this week and next week up-front because 6/7 days of the week
// the minimized calendar view needs to show the next week as well
// Since we're querying for all past, incomplete tasks that need to be completed,
// they will get duplicated unless deduped
const dedupeTasksById = (tasks: DashboardTask[]): DashboardTask[] =>
  Array.from(new Map(tasks.map((task) => [task.id, task])).values());

export const CalendarAndTasks = ({
  calendarExpanded,
  selectedUserId,
  panelView,
  onPanelViewChange,
}: {
  calendarExpanded: boolean;
  selectedUserId: DashboardSelectedUser;
  panelView: DashboardSchedulePanelView;
  onPanelViewChange: (next: DashboardSchedulePanelView) => void;
}) => {
  /* React Hooks */
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  // The mini agenda always spans this week + next (next-7-days can cross Saturday).
  const currentWeekStart = useMemo(
    () => dates.getSundayWeekStart(new Date()),
    [],
  );
  const nextWeekStart = useMemo(
    () => shiftWeeks(currentWeekStart, 1),
    [currentWeekStart],
  );
  // The expanded grid shows one week at a time; flipping changes this.
  const [calendarWeekStart, setCalendarWeekStart] = useState(currentWeekStart);
  // Task-list day, lifted so it persists across the mini/expanded toggle.
  const [taskDay, setTaskDay] = useState(() =>
    dates.getStartOfLocalDay(new Date()),
  );
  // The task list reads the week containing the selected day, so its counts stay
  // in lockstep with the calendar band and share the same per-week fetch.
  const taskWeekStart = useMemo(
    () => dates.getSundayWeekStart(taskDay),
    [taskDay],
  );

  // "c"/"t" flip the panel between calendar and tasks, unless the user is typing
  // (e.g. the pulse update box) or holding a modifier.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isUserTyping() || e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key !== "c" && key !== "t") return;
      const view =
        key === "c"
          ? DashboardSchedulePanelView.CALENDAR
          : DashboardSchedulePanelView.TASKS;
      mixpanelUtils.trackKeyboardShortcutUsed(
        key.toUpperCase(),
        MIXPANEL_SCREENS.DASHBOARD,
      );
      onPanelViewChange(view);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Database CRUD Operations */
  // Read all calendar data need for calendar and mini calendar views out the gate
  const currentWeek = useGetCalendarWeek(currentWeekStart);
  const nextWeek = useGetCalendarWeek(nextWeekStart);
  const visibleWeek = useGetCalendarWeek(calendarWeekStart);
  // Fetch for the task list's week; shares the cache with the calendar when the
  // weeks coincide, and warms it for the calendar when they don't.
  const taskWeek = useGetCalendarWeek(taskWeekStart);

  const currentUser = useGetCurrentUser();
  const isAllView = selectedUserId === ALL_USERS_KEY;
  // New general tasks are owned by the viewed staffer — or the current user when
  // viewing everyone.
  const newTaskAssigneeUserId =
    selectedUserId === ALL_USERS_KEY
      ? // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        currentUser?.id || null
      : selectedUserId;
  // All view shows customer interviews only; a staffer view shows their events only.
  const filterForOwner = (list: DashboardInterview[]) =>
    isAllView
      ? list.filter((interview) => interview.type === InterviewType.CUSTOMER)
      : list.filter((interview) => interview.ownerUserId === selectedUserId);

  const miniInterviews = filterForOwner([
    ...(currentWeek.data?.interviews ?? []),
    ...(nextWeek.data?.interviews ?? []),
  ]);

  // All view shows every staffer's tasks (with owner avatars); a staffer view
  // shows tasks they own — single ownership reads the first assignee.
  const filterTasksForOwner = (list: DashboardTask[]) =>
    isAllView
      ? list
      : list.filter((task) => task.assigneeUserIds[0] === selectedUserId);

  // Overdue carryover + today-only flags (missing starts) can repeat within a
  // fetch, so collapse by id before rendering.
  const taskListTasks = filterTasksForOwner(
    dedupeTasksById(taskWeek.data?.tasks ?? []),
  );

  // Same single-ownership convention as tasks — read the first assignee only.
  const filterCommsToSendForOwner = (list: CommsToSendCandidacy[]) =>
    isAllView
      ? list
      : list.filter((c) => c.assigneeUserIds[0] === selectedUserId);
  const commsToSend = filterCommsToSendForOwner(
    taskWeek.data?.commsToSend ?? [],
  );

  /* Handlers */
  const handleEditAvailability = () => {
    mixpanelUtils.trackButtonClick(
      "Open screen availability",
      MIXPANEL_SCREENS.DASHBOARD,
    );
    setAvailabilityOpen(true);
  };

  const handlePrevWeek = () => {
    setCalendarWeekStart((prev) => shiftWeeks(prev, -1));
  };

  const handleNextWeek = () => {
    setCalendarWeekStart((prev) => shiftWeeks(prev, 1));
  };

  // Clicking a day in the calendar's tasks band jumps into the task list on that day.
  const handleOpenTasksView = (day: Date) => {
    setTaskDay(dates.getStartOfLocalDay(day));
    onPanelViewChange(DashboardSchedulePanelView.TASKS);
  };

  // Opens a schedule card's candidate in a new tab
  const handleSelectCandidacy = (interview: DashboardInterview) => {
    // Prototype cards have negative IDs and do not navigate to synthetic records.
    if (interview.id < 0 || !interview.candidacy) return;
    mixpanelUtils.trackButtonClick(
      "Open candidate from schedule",
      MIXPANEL_SCREENS.DASHBOARD,
    );
    window.open(
      getCandidacyLink({
        candidacyId: interview.candidacy.id,
        companyId: interview.company.id,
        jobId: interview.job.id,
      }),
      "_blank",
    );
  };

  /* Render */
  const renderTasks = () =>
    calendarExpanded ? (
      <TasksContainerExpanded
        tasks={taskListTasks}
        commsToSend={commsToSend}
        day={taskDay}
        onDayChange={setTaskDay}
        panelView={panelView}
        onPanelViewChange={onPanelViewChange}
        onEditAvailability={handleEditAvailability}
        showTaskOwnerAvatars={isAllView}
        newTaskAssigneeUserId={newTaskAssigneeUserId}
      />
    ) : (
      <TasksContainerMini
        tasks={taskListTasks}
        commsToSend={commsToSend}
        day={taskDay}
        onDayChange={setTaskDay}
        panelView={panelView}
        onPanelViewChange={onPanelViewChange}
        showTaskOwnerAvatars={isAllView}
        newTaskAssigneeUserId={newTaskAssigneeUserId}
      />
    );

  // keepPreviousData keeps the prior week on screen during flips, so the
  // skeleton only shows on the first-ever load.
  // PROTOTYPE ONLY: dev-only cards stay local; shared query caches and records are untouched.
  const renderCalendar = () => (
    <ScheduleCalendar
      calendarExpanded={calendarExpanded}
      interviews={
        import.meta.env.DEV
          ? calendarPrototype.interviews(
              calendarExpanded ? calendarWeekStart : new Date(),
              newTaskAssigneeUserId,
            )
          : calendarExpanded
            ? filterForOwner(visibleWeek.data?.interviews ?? [])
            : miniInterviews
      }
      isPending={
        import.meta.env.DEV
          ? false
          : calendarExpanded
            ? visibleWeek.isPending
            : currentWeek.isPending
      }
      isError={
        import.meta.env.DEV
          ? false
          : calendarExpanded
            ? visibleWeek.isError
            : currentWeek.isError
      }
      weekStart={calendarWeekStart}
      tasks={
        calendarExpanded
          ? filterTasksForOwner(visibleWeek.data?.tasks ?? [])
          : undefined
      }
      panelView={panelView}
      onPanelViewChange={onPanelViewChange}
      onPrevWeek={handlePrevWeek}
      onNextWeek={handleNextWeek}
      onOpenTasksView={handleOpenTasksView}
      showOwnerAvatar={isAllView}
      onSelectCandidacy={handleSelectCandidacy}
    />
  );

  const render = () => (
    <>
      {panelView === DashboardSchedulePanelView.TASKS
        ? renderTasks()
        : renderCalendar()}
      <ScreeningAvailabilitySheet
        open={availabilityOpen}
        onOpenChange={setAvailabilityOpen}
      />
    </>
  );

  return render();
};
